import React, { useState, useEffect, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import DestinationCard from '../components/DestinationCard';
import SearchBar from '../components/SearchBar';
import { loadDestinations, filterDestinations, getUniqueTags } from '../utils/dataLoader';

const MapView = React.lazy(() => import('../components/MapView'));

const Destinations = () => {
  const [destinations, setDestinations] = useState([]);
  const [filteredDestinations, setFilteredDestinations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await loadDestinations();
        setDestinations(data);
        setFilteredDestinations(data);
        setAvailableTags(getUniqueTags(data));
      } catch (error) {
        console.error('Error loading destinations:', error);
        setError('Failed to load destinations');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const filtered = filterDestinations(destinations, searchTerm, selectedTags);
    setFilteredDestinations(filtered);
  }, [destinations, searchTerm, selectedTags]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">Error</h2>
          <p className="text-secondary-600 dark:text-secondary-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Destinations - Travelogue</title>
        <meta name="description" content="Explore amazing destinations around the world. Find your next adventure with our curated collection of travel destinations." />
      </Helmet>

      <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900">
        {/* Header */}
        <div className="bg-white dark:bg-secondary-800 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 dark:text-white mb-4">
                Explore Destinations
              </h1>
              <p className="text-xl text-secondary-600 dark:text-secondary-400 max-w-3xl mx-auto">
                Discover incredible places from around the world. Find your next adventure with our curated collection of destinations.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            availableTags={availableTags}
          />
        </div>

        {/* View Toggle */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowMap(false)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  !showMap
                    ? 'bg-primary-600 text-white'
                    : 'bg-secondary-200 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-300'
                }`}
              >
                Grid View
              </button>
              <button
                onClick={() => setShowMap(true)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  showMap
                    ? 'bg-primary-600 text-white'
                    : 'bg-secondary-200 text-secondary-700 dark:bg-secondary-700 dark:text-secondary-300'
                }`}
              >
                Map View
              </button>
            </div>
            <p className="text-secondary-600 dark:text-secondary-400">
              {filteredDestinations.length} destination{filteredDestinations.length !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {filteredDestinations.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <h3 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
                No destinations found
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-8">
                Try adjusting your search terms or filters to find what you're looking for.
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedTags([]);
                }}
                className="btn-primary"
              >
                Clear Filters
              </button>
            </motion.div>
          ) : showMap ? (
            <Suspense fallback={
              <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            }>
              <MapView destinations={filteredDestinations} />
            </Suspense>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredDestinations.map((destination, index) => (
                <motion.div
                  key={destination.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <DestinationCard destination={destination} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Destinations;
